// /qa-admin — the SEPARATE, no-login QA review page (Chairman 2026-09-01).
// Reached via the secret link `getqordo.com/qa-admin?qa=<QA_TOKEN>`. The token
// self-activates (sets the tester cookie), so there is no PIN. Chairman reads
// the queue, adds his own comments, and marks items done here; the coding
// agent reads the same rows next session and fixes them.
import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'

type FeedbackItem = {
  id: number
  element_key: string
  element_label: string
  current_value: string | null
  comment: string
  author: string
  page: string
  snapshot_json: string | null
  status: 'open' | 'done'
  created_at: string
  updated_at: string
}

function prettySnapshot(raw: string | null): string {
  if (!raw) return '(no snapshot)'
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

export default function QaAdmin() {
  const [state, setState] = useState<'checking' | 'ready' | 'denied'>('checking')
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'open' | 'done' | 'all'>('open')
  const [busy, setBusy] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [noteBusy, setNoteBusy] = useState(false)

  // Self-activate from ?qa=<token>, then confirm access. No login, no PIN.
  useEffect(() => {
    let alive = true
    ;(async () => {
      const qa = new URLSearchParams(window.location.search).get('qa')
      if (qa && qa.length > 8) {
        try {
          await api.post('/api/qa/activate', { token: qa })
        } catch {
          /* fall through to the cookie check */
        }
      }
      try {
        await api.get('/api/feedback?status=open')
        if (alive) setState('ready')
      } catch {
        if (alive) setState('denied')
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const reload = useCallback(() => {
    setLoading(true)
    api
      .get<{ items: FeedbackItem[] }>(`/api/feedback?status=${filter}`)
      .then((d) => setItems(d.items))
      .finally(() => setLoading(false))
  }, [filter])

  useEffect(() => {
    if (state === 'ready') reload()
  }, [state, reload])

  async function setStatus(id: number, status: 'open' | 'done') {
    setBusy(id)
    try {
      await api.patch(`/api/feedback/${id}`, { status })
      if (filter === 'all') setItems((l) => l.map((i) => (i.id === id ? { ...i, status } : i)))
      else setItems((l) => l.filter((i) => i.id !== id))
    } finally {
      setBusy(null)
    }
  }

  async function addNote() {
    const text = note.trim()
    if (!text || noteBusy) return
    setNoteBusy(true)
    try {
      await api.post('/api/feedback', {
        element_key: 'qa-note',
        element_label: '📝 Note from Chairman',
        current_value: '',
        comment: text,
        author: 'Djoko',
        page: '/qa-admin',
        snapshot_json: JSON.stringify({ source: 'qa-admin note', ts: new Date().toISOString() }),
      })
      setNote('')
      if (filter !== 'done') reload()
    } finally {
      setNoteBusy(false)
    }
  }

  const openCount = items.filter((i) => i.status === 'open').length

  if (state === 'checking') {
    return <div className="min-h-screen bg-ink-950 grid place-items-center text-ink-500">Loading…</div>
  }
  if (state === 'denied') {
    return (
      <div className="min-h-screen bg-ink-950 grid place-items-center p-6 text-center">
        <div>
          <div className="text-4xl mb-3">🔒</div>
          <h1 className="text-lg font-semibold text-white">QA review</h1>
          <p className="mt-2 text-sm text-ink-500 max-w-xs">
            This page needs the private QA link. Open it with your <code>?qa=…</code> link.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-ink-950 text-ink-100">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <header className="mb-5">
          <h1 className="text-2xl font-bold text-white">QA review</h1>
          <p className="text-sm text-ink-500 mt-0.5">
            {openCount} open · comments people leave via the QA link land here. Mark done when fixed.
          </p>
        </header>

        {/* Add a note directly */}
        <div className="rounded-2xl border border-ink-800 bg-ink-900 p-4 mb-5">
          <label className="block text-sm font-medium text-white mb-2">📝 Add a comment</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            placeholder="Anything to change or fix — I'll pick it up next session."
            rows={2}
            className="w-full rounded-lg border border-ink-700 bg-ink-950 p-2 text-sm text-white outline-none focus:border-brand-500"
          />
          <button
            onClick={() => void addNote()}
            disabled={noteBusy || !note.trim()}
            className="mt-2 w-full rounded-lg bg-brand-600 py-2.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-50"
          >
            {noteBusy ? 'Saving…' : 'Add comment'}
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {(['open', 'done', 'all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                filter === s ? 'bg-brand-600 text-white' : 'bg-ink-900 border border-ink-800 text-ink-100'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-ink-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-ink-800 bg-ink-900 p-6 text-center text-ink-500">
            No {filter === 'all' ? '' : filter} comments.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="rounded-2xl border border-ink-800 bg-ink-900 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-white break-words">{item.element_label}</div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      {item.page} · {item.author} · {item.created_at.slice(0, 16).replace('T', ' ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`chip border text-xs ${
                        item.status === 'open'
                          ? 'text-amber-400 border-amber-500/30'
                          : 'text-emerald-400 border-emerald-500/30'
                      }`}
                    >
                      {item.status}
                    </span>
                    <button
                      onClick={() => setStatus(item.id, item.status === 'open' ? 'done' : 'open')}
                      disabled={busy === item.id}
                      className={`text-xs px-2 py-1 rounded text-white disabled:opacity-50 ${
                        item.status === 'open' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-ink-800 hover:bg-ink-700'
                      }`}
                    >
                      {busy === item.id ? '…' : item.status === 'open' ? 'Mark done' : 'Reopen'}
                    </button>
                  </div>
                </div>

                <p className="mt-2 text-sm text-ink-100 whitespace-pre-wrap break-words">{item.comment}</p>

                {item.current_value && (
                  <div className="mt-1 text-xs text-ink-500 break-words">
                    Value at click: <span className="text-ink-100">{item.current_value}</span>
                  </div>
                )}

                {item.element_key !== 'qa-note' && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-ink-500 hover:text-ink-100">
                      Snapshot ({item.element_key.slice(0, 60)})
                    </summary>
                    <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-ink-950 p-3 text-xs text-ink-100">
                      {prettySnapshot(item.snapshot_json)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
